import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'rebass';
import { lighten, opacify } from 'polished';

import { COLOR } from '../Theme';
import { SplitScore, Text } from '../Base';

import { Table, TableBody, TableHead, TableCell as TC, TableRow as TR } from './TableComponents';

const GAT_ANSWER_COUNT = 4;
const CORRECT_COLOR = opacify(0.9, COLOR.correct);

const getMaxSlotCount = (rows = [{ solution: [] }]) => {
  const solutions = rows.map(row => row.solution);
  return solutions.reduce((max, b) => {
    return b.length > max ? b.length : max;
  }, GAT_ANSWER_COUNT);
};

const TableCell = props => <TC cellBorder style={{ padding: 4 }} {...props} />;

const HeaderCell = props => (
  <TableCell
    textAlign="center"
    bgColor={props.bgColor || COLOR.darkBlue}
    color={COLOR.white}
    {...props}
  />
);

const ChoiceHeaderCell = props => <HeaderCell width="60px" {...props} />;

const TableRow = props => <TR height="2em" {...props} />;

const TextCell = props => (
  <Text.Data style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} {...props} />
);

const GRADE_ANSWER_TYPE = {
  CORRECT: 'CORRECT',
  ANSWERED_WRONG: 'ANSWERED_WRONG',
  SHOULD_BLANK: 'SHOULD_BLANK',
  SHOULD_ANSWER: 'SHOULD_ANSWER',
};

const gradeAnswer = (answer, key) => {
  let result;
  if (answer && !key) {
    result = GRADE_ANSWER_TYPE.SHOULD_BLANK;
  } else if (!answer && key) {
    result = GRADE_ANSWER_TYPE.SHOULD_ANSWER;
  } else if (answer && key) {
    result = answer === key ? GRADE_ANSWER_TYPE.CORRECT : GRADE_ANSWER_TYPE.ANSWERED_WRONG;
  }
  return result;
};

const getAnswerColor = type => {
  switch (type) {
    case GRADE_ANSWER_TYPE.SHOULD_BLANK:
    case GRADE_ANSWER_TYPE.SHOULD_ANSWER:
    case GRADE_ANSWER_TYPE.ANSWERED_WRONG:
      return COLOR.GAT.incorrect;
    case GRADE_ANSWER_TYPE.CORRECT:
    default:
      return null;
  }
};

const getResultColor = isCorrect => {
  if (!isCorrect) return COLOR.GAT.incorrect;
  return null;
};

const AnswerRow = ({
  questionNumber,
  title,
  maxAnswerSlot,
  answer,
  solution,
  score,
  maxScore,
  result,
  ...restProps
}) => {
  const filledAnswer = answer || Array(maxAnswerSlot).fill('');
  const filledSolution = solution || Array(maxAnswerSlot).fill('');
  return (
    <TableRow>
      <TableCell>
        <TextCell>{questionNumber}</TextCell>
      </TableCell>
      <TableCell maxWidth={['200px', '20vw', '30vw', '20vw']}>
        <TextCell>{title}</TextCell>
      </TableCell>
      {filledAnswer.map((item, i) => (
        <TableCell key={item + i} bgColor={getResultColor(result[i])} textAlign="center">
          <TextCell>{item}</TextCell>
        </TableCell>
      ))}
      {filledSolution.map((item, i) => (
        <TableCell textAlign="center" key={item + i} bgColor={lighten(0.55, COLOR.correct)}>
          <TextCell>{item}</TextCell>
        </TableCell>
      ))}
      <TableCell textAlign="right" minWidth={'90px'}>
        <SplitScore
          score={score}
          fullScore={maxScore}
          style={{ display: 'inline-flex' }}
          variant="other"
        />
      </TableCell>
    </TableRow>
  );
};

class GATTable extends Component {
  static propTypes = {
    value: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        result: PropTypes.array,
        answer: PropTypes.array,
        solution: PropTypes.array,
        score: PropTypes.number,
        maxScore: PropTypes.number,
      }),
    ),
  };

  render() {
    const { value } = this.props;
    const maxAnswerSlot = getMaxSlotCount(value);
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table padding={'checkbox'}>
          <TableHead>
            <TableRow>
              <HeaderCell rowSpan="2" />
              <HeaderCell rowSpan="2">ข้อความ</HeaderCell>
              <HeaderCell colSpan={maxAnswerSlot}>คำตอบของฉัน</HeaderCell>
              <HeaderCell colSpan={maxAnswerSlot} bgColor={CORRECT_COLOR}>
                เฉลย
              </HeaderCell>
              <HeaderCell rowSpan="2" width="200px">
                คะแนน
              </HeaderCell>
            </TableRow>
            <TableRow>
              {Array(maxAnswerSlot)
                .fill(1)
                .map((n, i) => (
                  <ChoiceHeaderCell key={i}>{i + 1}</ChoiceHeaderCell>
                ))}
              {Array(maxAnswerSlot)
                .fill(1)
                .map((n, i) => (
                  <ChoiceHeaderCell key={i} bgColor={CORRECT_COLOR}>
                    {i + 1}
                  </ChoiceHeaderCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {value.map(item => (
              <AnswerRow
                key={item.id}
                questionNumber={item.label}
                maxAnswerSlot={maxAnswerSlot}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }
}

export default GATTable;
